import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { fmt, proxyImg } from "../lib/format";

export default function CartDrawer() {
  const { items, open, setOpen, subtotal, updateQty, remove } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  const goCheckout = () => {
    setOpen(false);
    nav(user ? "/checkout" : "/login?next=/checkout");
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-[60]"
            onClick={() => setOpen(false)}
            data-testid="cart-backdrop"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 bottom-0 w-full sm:w-[440px] bg-white z-[70] flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between p-6 border-b border-black/5">
              <h2 className="font-heading text-2xl">Seu carrinho</h2>
              <button
                onClick={() => setOpen(false)}
                className="p-2 hover:bg-bg-secondary rounded-full"
                data-testid="close-cart"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {items.length === 0 ? (
                <div className="text-center py-16">
                  <ShoppingBag size={48} className="mx-auto text-ink-secondary mb-4" strokeWidth={1.2} />
                  <p className="text-ink-secondary mb-6">Seu carrinho está vazio</p>
                  <button onClick={() => setOpen(false)} className="btn-primary" data-testid="empty-cart-shop-btn">
                    Continuar comprando
                  </button>
                </div>
              ) : (
                items.map((it) => (
                  <div
                    key={it.product_id}
                    className="flex gap-4 p-3 rounded-2xl bg-bg-secondary"
                    data-testid={`cart-item-${it.product_id}`}
                  >
                    <img src={proxyImg(it.image)} alt="" className="w-20 h-20 object-contain bg-white rounded-xl p-1" />
                    <div className="flex-1 flex flex-col">
                      <Link
                        to={`/produto/${it.slug}`}
                        onClick={() => setOpen(false)}
                        className="font-heading text-base leading-tight hover:text-brand-accent"
                      >
                        {it.name}
                      </Link>
                      <div className="mt-auto flex items-end justify-between">
                        <div className="flex items-center gap-2 bg-white rounded-full p-1">
                          <button
                            onClick={() => updateQty(it.product_id, it.quantity - 1)}
                            className="p-1.5 hover:bg-bg-secondary rounded-full"
                            data-testid={`qty-minus-${it.product_id}`}
                          >
                            <Minus size={14} />
                          </button>
                          <span className="text-sm font-tech w-6 text-center">{it.quantity}</span>
                          <button
                            onClick={() => updateQty(it.product_id, it.quantity + 1)}
                            className="p-1.5 hover:bg-bg-secondary rounded-full"
                            data-testid={`qty-plus-${it.product_id}`}
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                        <div className="text-right">
                          <div className="font-heading text-lg text-brand-primary">{fmt(it.price * it.quantity)}</div>
                          <button
                            onClick={() => remove(it.product_id)}
                            className="text-xs text-status-error inline-flex items-center gap-1 hover:underline"
                            data-testid={`remove-${it.product_id}`}
                          >
                            <Trash2 size={12} /> Remover
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            {items.length > 0 && (
              <div className="p-6 border-t border-black/5 space-y-4">
                <div className="flex justify-between items-baseline">
                  <span className="text-ink-secondary">Subtotal</span>
                  <span className="font-heading text-3xl text-brand-primary">{fmt(subtotal)}</span>
                </div>
                <p className="text-xs text-ink-secondary">
                  Ganhe <span className="text-brand-accent font-semibold">5% OFF</span> pagando no PIX no checkout.
                </p>
                <button onClick={goCheckout} className="btn-primary w-full" data-testid="checkout-btn">
                  Finalizar compra
                </button>
                <button onClick={() => setOpen(false)} className="text-sm text-ink-secondary hover:text-brand-primary w-full">
                  Continuar comprando
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
